import React, { useMemo, useState } from "react";
import {
    Wallet,
    Plus,
    IndianRupee,
    ArrowDownToLine,
    ArrowUpFromLine,
    History,
    Search,
    Filter,
    X,
    Eye,
    CreditCard,
    CheckCircle2,
    Clock3,
    XCircle,
    UserRound,
    Crown,
    CalendarDays,
    Building2,
    Receipt,
} from "lucide-react";

/* =========================================================
   INITIAL WALLET
========================================================= */

const initialWallet = {
    balance: 42500,
    totalRecharged: 75000,
    totalUsed: 32500,
};

/* =========================================================
   TRANSACTIONS
========================================================= */

const initialTransactions = [
    {
        id: 1,
        transactionId: "WLT000001",
        type: "Recharge",
        memberId: "-",
        memberName: "-",
        plan: "-",
        amount: 25000,
        balanceBefore: 17500,
        balanceAfter: 42500,
        paymentMethod: "UPI",
        referenceNo: "UPI123456789",
        status: "Success",
        date: "2026-10-01",
        time: "10:30 AM",
        description: "Wallet Recharge",
    },
    {
        id: 2,
        transactionId: "WLT000002",
        type: "Subscription",
        memberId: "JP00001",
        memberName: "Arun Kumar",
        plan: "Premium",
        amount: 5000,
        balanceBefore: 47500,
        balanceAfter: 42500,
        paymentMethod: "Wallet",
        referenceNo: "SUB000001",
        status: "Success",
        date: "2026-10-01",
        time: "11:15 AM",
        description:
            "Premium Membership Subscription",
    },
    {
        id: 3,
        transactionId: "WLT000003",
        type: "Subscription",
        memberId: "JP00002",
        memberName: "Priya S",
        plan: "Gold",
        amount: 10000,
        balanceBefore: 57500,
        balanceAfter: 47500,
        paymentMethod: "Wallet",
        referenceNo: "SUB000002",
        status: "Success",
        date: "2026-09-29",
        time: "03:20 PM",
        description:
            "Gold Membership Subscription",
    },
    {
        id: 4,
        transactionId: "WLT000004",
        type: "Recharge",
        memberId: "-",
        memberName: "-",
        plan: "-",
        amount: 25000,
        balanceBefore: 32500,
        balanceAfter: 57500,
        paymentMethod: "Bank Transfer",
        referenceNo: "BANK982341",
        status: "Success",
        date: "2026-09-28",
        time: "09:45 AM",
        description: "Wallet Recharge",
    },
    {
        id: 5,
        transactionId: "WLT000005",
        type: "Subscription",
        memberId: "JP00003",
        memberName: "Karthik R",
        plan: "Basic",
        amount: 2500,
        balanceBefore: 35000,
        balanceAfter: 32500,
        paymentMethod: "Wallet",
        referenceNo: "SUB000003",
        status: "Success",
        date: "2026-09-27",
        time: "04:10 PM",
        description:
            "Basic Membership Subscription",
    },
];

/* =========================================================
   PLANS
========================================================= */

const membershipPlans = [
    {
        id: 1,
        name: "Basic",
        amount: 2500,
        color: "blue",
    },
    {
        id: 2,
        name: "Premium",
        amount: 5000,
        color: "pink",
    },
    {
        id: 3,
        name: "Gold",
        amount: 10000,
        color: "gold",
    },
];

/* =========================================================
   MEMBERS
========================================================= */

const members = [
    {
        id: "JP00001",
        name: "Arun Kumar",
        mobile: "9876543210",
        currentPlan: "None",
    },
    {
        id: "JP00002",
        name: "Priya S",
        mobile: "9876543211",
        currentPlan: "None",
    },
    {
        id: "JP00003",
        name: "Karthik R",
        mobile: "9876543212",
        currentPlan: "Basic",
    },
    {
        id: "JP00004",
        name: "Divya M",
        mobile: "9876543213",
        currentPlan: "None",
    },
    {
        id: "JP00005",
        name: "Suresh Kumar",
        mobile: "9876543214",
        currentPlan: "Premium",
    },
];

/* =========================================================
   MAIN COMPONENT
========================================================= */

function Wallets() {
    const [wallet, setWallet] =
        useState(initialWallet);

    const [transactions, setTransactions] =
        useState(initialTransactions);

    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] =
        useState("");
    const [statusFilter, setStatusFilter] =
        useState("");

    const [showFilters, setShowFilters] =
        useState(false);

    const [showRechargeModal, setShowRechargeModal] =
        useState(false);

    const [showSubscriptionModal, setShowSubscriptionModal] =
        useState(false);

    const [showTransactionModal, setShowTransactionModal] =
        useState(false);

    const [selectedTransaction, setSelectedTransaction] =
        useState(null);

    const [rechargeForm, setRechargeForm] =
        useState({
            amount: "",
            paymentMethod: "UPI",
            referenceNo: "",
            remarks: "",
        });

    const [subscriptionForm, setSubscriptionForm] =
        useState({
            memberId: "",
            plan: "",
        });

    /* =====================================================
       TOTALS
    ===================================================== */

    const totalRechargeCount =
        transactions.filter(
            (item) =>
                item.type === "Recharge" &&
                item.status === "Success"
        ).length;

    const totalSubscriptionCount =
        transactions.filter(
            (item) =>
                item.type === "Subscription" &&
                item.status === "Success"
        ).length;

    /* =====================================================
       FILTERED DATA
    ===================================================== */

    const filteredTransactions = useMemo(() => {
        const searchText =
            search.trim().toLowerCase();

        return transactions.filter((item) => {
            const matchesSearch =
                !searchText ||
                item.transactionId
                    .toLowerCase()
                    .includes(searchText) ||
                item.memberId
                    .toLowerCase()
                    .includes(searchText) ||
                item.memberName
                    .toLowerCase()
                    .includes(searchText) ||
                item.referenceNo
                    .toLowerCase()
                    .includes(searchText);

            const matchesType =
                !typeFilter ||
                item.type === typeFilter;

            const matchesStatus =
                !statusFilter ||
                item.status === statusFilter;

            return (
                matchesSearch &&
                matchesType &&
                matchesStatus
            );
        });
    }, [
        transactions,
        search,
        typeFilter,
        statusFilter,
    ]);

    /* =====================================================
       RECHARGE CHANGE
    ===================================================== */

    const handleRechargeChange = (e) => {
        const { name, value } = e.target;

        setRechargeForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    /* =====================================================
       RECHARGE WALLET
    ===================================================== */

    const handleRecharge = (e) => {
        e.preventDefault();

        const amount = Number(
            rechargeForm.amount
        );

        if (!amount || amount <= 0) {
            alert(
                "Please enter a valid recharge amount."
            );
            return;
        }

        const today = new Date();

        const date = today
            .toISOString()
            .split("T")[0];

        const newBalance =
            wallet.balance + amount;

        const newTransaction = {
            id:
                Math.max(
                    0,
                    ...transactions.map(
                        (item) => item.id
                    )
                ) + 1,

            transactionId: `WLT${String(
                transactions.length + 1
            ).padStart(6, "0")}`,

            type: "Recharge",

            memberId: "-",
            memberName: "-",
            plan: "-",

            amount,

            balanceBefore: wallet.balance,
            balanceAfter: newBalance,

            paymentMethod:
                rechargeForm.paymentMethod,

            referenceNo:
                rechargeForm.referenceNo ||
                "-",

            status: "Success",

            date,

            time: today.toLocaleTimeString(
                "en-IN",
                {
                    hour: "2-digit",
                    minute: "2-digit",
                }
            ),

            description:
                rechargeForm.remarks ||
                "Wallet Recharge",
        };

        setWallet((prev) => ({
            ...prev,
            balance: newBalance,
            totalRecharged:
                prev.totalRecharged + amount,
        }));

        setTransactions((prev) => [
            newTransaction,
            ...prev,
        ]);

        setRechargeForm({
            amount: "",
            paymentMethod: "UPI",
            referenceNo: "",
            remarks: "",
        });

        setShowRechargeModal(false);

        alert(
            "Wallet recharged successfully."
        );
    };

    /* =====================================================
       SUBSCRIPTION
    ===================================================== */

    const selectedPlan =
        membershipPlans.find(
            (item) =>
                item.name ===
                subscriptionForm.plan
        );

    const selectedMember =
        members.find(
            (item) =>
                item.id ===
                subscriptionForm.memberId
        );

    const handleSubscriptionChange = (e) => {
        const { name, value } = e.target;

        setSubscriptionForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    /* =====================================================
       USE WALLET FOR SUBSCRIPTION
    ===================================================== */

    const handleSubscription = (e) => {
        e.preventDefault();

        if (!selectedMember) {
            alert("Please select a member.");
            return;
        }

        if (!selectedPlan) {
            alert(
                "Please select membership plan."
            );
            return;
        }

        if (
            wallet.balance <
            selectedPlan.amount
        ) {
            alert(
                "Insufficient wallet balance."
            );
            return;
        }

        const today = new Date();

        const date = today
            .toISOString()
            .split("T")[0];

        const newBalance =
            wallet.balance -
            selectedPlan.amount;

        const newTransaction = {
            id:
                Math.max(
                    0,
                    ...transactions.map(
                        (item) => item.id
                    )
                ) + 1,

            transactionId: `WLT${String(
                transactions.length + 1
            ).padStart(6, "0")}`,

            type: "Subscription",

            memberId: selectedMember.id,
            memberName: selectedMember.name,
            plan: selectedPlan.name,

            amount: selectedPlan.amount,

            balanceBefore: wallet.balance,
            balanceAfter: newBalance,

            paymentMethod: "Wallet",

            referenceNo: `SUB${String(
                transactions.length + 1
            ).padStart(6, "0")}`,

            status: "Success",

            date,

            time: today.toLocaleTimeString(
                "en-IN",
                {
                    hour: "2-digit",
                    minute: "2-digit",
                }
            ),

            description: `${selectedPlan.name} Membership Subscription`,
        };

        setWallet((prev) => ({
            ...prev,
            balance: newBalance,
            totalUsed:
                prev.totalUsed +
                selectedPlan.amount,
        }));

        setTransactions((prev) => [
            newTransaction,
            ...prev,
        ]);

        setSubscriptionForm({
            memberId: "",
            plan: "",
        });

        setShowSubscriptionModal(false);

        alert(
            `${selectedPlan.name} subscription activated successfully.`
        );
    };

    /* =====================================================
       VIEW TRANSACTION
    ===================================================== */

    const openTransaction = (transaction) => {
        setSelectedTransaction(
            transaction
        );

        setShowTransactionModal(true);
    };

    /* =====================================================
       RESET FILTER
    ===================================================== */

    const resetFilters = () => {
        setSearch("");
        setTypeFilter("");
        setStatusFilter("");
    };

    return (
        <div className="wallet-page">

            {/* =================================================
                HEADER
            ================================================= */}

            <div className="wallet-page-header">

                <div>
                    <h1>Branch Wallet</h1>

                    <p>
                        Manage branch wallet balance,
                        recharge funds and use wallet
                        balance for member subscriptions.
                    </p>
                </div>

                <div className="wallet-header-actions">

                    <button
                        type="button"
                        className="wallet-recharge-btn"
                        onClick={() =>
                            setShowRechargeModal(
                                true
                            )
                        }
                    >
                        <Plus size={18} />
                        Add Wallet
                    </button>

                    <button
                        type="button"
                        className="wallet-subscription-btn"
                        onClick={() =>
                            setShowSubscriptionModal(
                                true
                            )
                        }
                    >
                        <Crown size={17} />
                        Member Subscription
                    </button>

                </div>

            </div>

            {/* =================================================
                CURRENT BALANCE
            ================================================= */}

            <div className="wallet-balance-card">

                <div className="wallet-balance-left">

                    <div className="wallet-main-icon">
                        <Wallet size={27} />
                    </div>

                    <div>
                        <span>
                            Available Wallet Balance
                        </span>

                        <strong>
                            ₹
                            {wallet.balance.toLocaleString(
                                "en-IN"
                            )}
                        </strong>

                        <small>
                            Erode Main Branch
                        </small>
                    </div>

                </div>

                <div className="wallet-balance-right">

                    <div>
                        <span>
                            Total Recharged
                        </span>

                        <strong>
                            ₹
                            {wallet.totalRecharged.toLocaleString(
                                "en-IN"
                            )}
                        </strong>
                    </div>

                    <div>
                        <span>
                            Used for Subscriptions
                        </span>

                        <strong>
                            ₹
                            {wallet.totalUsed.toLocaleString(
                                "en-IN"
                            )}
                        </strong>
                    </div>

                </div>

            </div>

            {/* =================================================
                SUMMARY
            ================================================= */}

            <div className="wallet-summary-grid">

                <SummaryCard
                    icon={Wallet}
                    title="Wallet Balance"
                    value={`₹${wallet.balance.toLocaleString(
                        "en-IN"
                    )}`}
                    type="blue"
                />

                <SummaryCard
                    icon={ArrowDownToLine}
                    title="Total Recharged"
                    value={`₹${wallet.totalRecharged.toLocaleString(
                        "en-IN"
                    )}`}
                    type="green"
                />

                <SummaryCard
                    icon={ArrowUpFromLine}
                    title="Subscription Usage"
                    value={`₹${wallet.totalUsed.toLocaleString(
                        "en-IN"
                    )}`}
                    type="pink"
                />

                <SummaryCard
                    icon={History}
                    title="Transactions"
                    value={
                        transactions.length
                    }
                    type="orange"
                />

            </div>

            {/* =================================================
                QUICK ACTIONS
            ================================================= */}

            <div className="wallet-quick-actions">

                <div
                    className="wallet-quick-card recharge"
                    onClick={() =>
                        setShowRechargeModal(
                            true
                        )
                    }
                >
                    <div>
                        <ArrowDownToLine
                            size={22}
                        />
                    </div>

                    <section>
                        <h3>
                            Recharge Wallet
                        </h3>

                        <p>
                            Add funds to branch wallet
                        </p>
                    </section>

                    <Plus size={18} />
                </div>

                <div
                    className="wallet-quick-card subscription"
                    onClick={() =>
                        setShowSubscriptionModal(
                            true
                        )
                    }
                >
                    <div>
                        <ArrowUpFromLine
                            size={22}
                        />
                    </div>

                    <section>
                        <h3>
                            Member Subscription
                        </h3>

                        <p>
                            Use wallet balance for
                            membership
                        </p>
                    </section>

                    <Plus size={18} />
                </div>

            </div>

            {/* =================================================
                TRANSACTION HISTORY
            ================================================= */}

            <div className="wallet-history-card">

                <div className="wallet-history-header">

                    <div>
                        <h2>
                            Wallet Transaction History
                        </h2>

                        <span>
                            {
                                filteredTransactions.length
                            } transactions
                        </span>
                    </div>

                </div>

                {/* FILTER */}

                <div className="wallet-filter">

                    <div className="wallet-search">

                        <Search size={17} />

                        <input
                            type="text"
                            placeholder="Search Transaction ID, Member ID, Name..."
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={() =>
                                    setSearch("")
                                }
                            >
                                <X size={15} />
                            </button>
                        )}

                    </div>

                    <button
                        type="button"
                        className={`wallet-filter-btn ${
                            showFilters
                                ? "active"
                                : ""
                        }`}
                        onClick={() =>
                            setShowFilters(
                                (prev) => !prev
                            )
                        }
                    >
                        <Filter size={16} />
                        Filters
                    </button>

                </div>

                {showFilters && (
                    <div className="wallet-filter-row">

                        <div>
                            <label>
                                Transaction Type
                            </label>

                            <select
                                value={typeFilter}
                                onChange={(e) =>
                                    setTypeFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    All Transactions
                                </option>

                                <option value="Recharge">
                                    Recharge
                                </option>

                                <option value="Subscription">
                                    Subscription
                                </option>
                            </select>
                        </div>

                        <div>
                            <label>
                                Status
                            </label>

                            <select
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    All Status
                                </option>

                                <option value="Success">
                                    Success
                                </option>

                                <option value="Pending">
                                    Pending
                                </option>

                                <option value="Failed">
                                    Failed
                                </option>
                            </select>
                        </div>

                        <button
                            type="button"
                            onClick={resetFilters}
                            className="wallet-reset-btn"
                        >
                            Reset
                        </button>

                    </div>
                )}

                {/* TABLE */}

                <div className="wallet-table-wrapper">

                    <table className="wallet-table">

                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Transaction</th>
                                <th>Type</th>
                                <th>Member</th>
                                <th>Plan</th>
                                <th>Amount</th>
                                <th>Balance Before</th>
                                <th>Balance After</th>
                                <th>Method</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>

                        <tbody>

                            {filteredTransactions.length ===
                            0 ? (
                                <tr>
                                    <td
                                        colSpan="12"
                                        className="wallet-empty"
                                    >
                                        No transactions
                                        found.
                                    </td>
                                </tr>
                            ) : (
                                filteredTransactions.map(
                                    (
                                        transaction,
                                        index
                                    ) => (
                                        <tr
                                            key={
                                                transaction.id
                                            }
                                        >

                                            <td>
                                                {index +
                                                    1}
                                            </td>

                                            <td>
                                                <strong className="transaction-id">
                                                    {
                                                        transaction.transactionId
                                                    }
                                                </strong>

                                                <small>
                                                    {
                                                        transaction.referenceNo
                                                    }
                                                </small>
                                            </td>

                                            <td>
                                                <span
                                                    className={`wallet-type-badge ${transaction.type.toLowerCase()}`}
                                                >
                                                    {transaction.type ===
                                                    "Recharge" ? (
                                                        <ArrowDownToLine
                                                            size={
                                                                13
                                                            }
                                                        />
                                                    ) : (
                                                        <ArrowUpFromLine
                                                            size={
                                                                13
                                                            }
                                                        />
                                                    )}

                                                    {
                                                        transaction.type
                                                    }
                                                </span>
                                            </td>

                                            <td>
                                                {transaction.type ===
                                                "Subscription" ? (
                                                    <div className="wallet-member-cell">

                                                        <strong>
                                                            {
                                                                transaction.memberName
                                                            }
                                                        </strong>

                                                        <span>
                                                            {
                                                                transaction.memberId
                                                            }
                                                        </span>

                                                    </div>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>

                                            <td>
                                                {transaction.plan !==
                                                "-" ? (
                                                    <span className="wallet-plan">
                                                        <Crown
                                                            size={
                                                                12
                                                            }
                                                        />
                                                        {
                                                            transaction.plan
                                                        }
                                                    </span>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>

                                            <td>
                                                <strong
                                                    className={
                                                        transaction.type ===
                                                        "Recharge"
                                                            ? "amount-credit"
                                                            : "amount-debit"
                                                    }
                                                >
                                                    {transaction.type ===
                                                    "Recharge"
                                                        ? "+"
                                                        : "-"}
                                                    ₹
                                                    {transaction.amount.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </strong>
                                            </td>

                                            <td>
                                                ₹
                                                {transaction.balanceBefore.toLocaleString(
                                                    "en-IN"
                                                )}
                                            </td>

                                            <td>
                                                <strong>
                                                    ₹
                                                    {transaction.balanceAfter.toLocaleString(
                                                        "en-IN"
                                                    )}
                                                </strong>
                                            </td>

                                            <td>
                                                {
                                                    transaction.paymentMethod
                                                }
                                            </td>

                                            <td>
                                                <div className="wallet-date">

                                                    <span>
                                                        {
                                                            transaction.date
                                                        }
                                                    </span>

                                                    <small>
                                                        {
                                                            transaction.time
                                                        }
                                                    </small>

                                                </div>
                                            </td>

                                            <td>
                                                <span
                                                    className={`wallet-status ${transaction.status.toLowerCase()}`}
                                                >
                                                    <span />
                                                    {
                                                        transaction.status
                                                    }
                                                </span>
                                            </td>

                                            <td>
                                                <button
                                                    type="button"
                                                    className="wallet-view-btn"
                                                    onClick={() =>
                                                        openTransaction(
                                                            transaction
                                                        )
                                                    }
                                                >
                                                    <Eye
                                                        size={
                                                            15
                                                        }
                                                    />
                                                </button>
                                            </td>

                                        </tr>
                                    )
                                )
                            )}

                        </tbody>

                    </table>

                </div>

            </div>

            {/* =================================================
                RECHARGE MODAL
            ================================================= */}

            {showRechargeModal && (
                <div
                    className="wallet-modal-overlay"
                    onMouseDown={(e) => {
                        if (
                            e.target ===
                            e.currentTarget
                        ) {
                            setShowRechargeModal(
                                false
                            );
                        }
                    }}
                >

                    <div className="wallet-modal">

                        <div className="wallet-modal-header">

                            <div>
                                <h2>
                                    Add Wallet
                                </h2>

                                <p>
                                    Recharge branch
                                    wallet balance.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setShowRechargeModal(
                                        false
                                    )
                                }
                            >
                                <X size={20} />
                            </button>

                        </div>

                        <form
                            className="wallet-form"
                            onSubmit={
                                handleRecharge
                            }
                        >

                            <div className="wallet-current-balance">

                                <Wallet size={21} />

                                <div>
                                    <span>
                                        Current Balance
                                    </span>

                                    <strong>
                                        ₹
                                        {wallet.balance.toLocaleString(
                                            "en-IN"
                                        )}
                                    </strong>
                                </div>

                            </div>

                            <div className="wallet-form-group">

                                <label>
                                    Recharge Amount
                                    <span>*</span>
                                </label>

                                <div className="amount-input">

                                    <IndianRupee
                                        size={16}
                                    />

                                    <input
                                        type="number"
                                        name="amount"
                                        value={
                                            rechargeForm.amount
                                        }
                                        onChange={
                                            handleRechargeChange
                                        }
                                        placeholder="Enter amount"
                                        min="1"
                                        required
                                    />

                                </div>

                            </div>

                            <div className="wallet-amount-options">

                                {[5000, 10000, 25000, 50000].map(
                                    (amount) => (
                                        <button
                                            type="button"
                                            key={
                                                amount
                                            }
                                            onClick={() =>
                                                setRechargeForm(
                                                    (
                                                        prev
                                                    ) => ({
                                                        ...prev,
                                                        amount,
                                                    })
                                                )
                                            }
                                        >
                                            ₹
                                            {amount.toLocaleString(
                                                "en-IN"
                                            )}
                                        </button>
                                    )
                                )}

                            </div>

                            <div className="wallet-form-group">

                                <label>
                                    Payment Method
                                </label>

                                <select
                                    name="paymentMethod"
                                    value={
                                        rechargeForm.paymentMethod
                                    }
                                    onChange={
                                        handleRechargeChange
                                    }
                                >
                                    <option value="UPI">
                                        UPI
                                    </option>

                                    <option value="Bank Transfer">
                                        Bank Transfer
                                    </option>

                                    <option value="Credit Card">
                                        Credit Card
                                    </option>

                                    <option value="Debit Card">
                                        Debit Card
                                    </option>

                                    <option value="Net Banking">
                                        Net Banking
                                    </option>

                                    <option value="Cash">
                                        Cash
                                    </option>
                                </select>

                            </div>

                            <div className="wallet-form-group">

                                <label>
                                    Transaction /
                                    Reference Number
                                </label>

                                <input
                                    type="text"
                                    name="referenceNo"
                                    value={
                                        rechargeForm.referenceNo
                                    }
                                    onChange={
                                        handleRechargeChange
                                    }
                                    placeholder="Enter reference number"
                                />

                            </div>

                            <div className="wallet-form-group">

                                <label>
                                    Remarks
                                </label>

                                <textarea
                                    name="remarks"
                                    value={
                                        rechargeForm.remarks
                                    }
                                    onChange={
                                        handleRechargeChange
                                    }
                                    placeholder="Enter remarks"
                                    rows="3"
                                />

                            </div>

                            <div className="wallet-form-footer">

                                <button
                                    type="button"
                                    className="wallet-cancel-btn"
                                    onClick={() =>
                                        setShowRechargeModal(
                                            false
                                        )
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="wallet-submit-btn"
                                >
                                    <Plus
                                        size={16}
                                    />
                                    Add Wallet
                                </button>

                            </div>

                        </form>

                    </div>

                </div>
            )}

            {/* =================================================
                SUBSCRIPTION MODAL
            ================================================= */}

            {showSubscriptionModal && (
                <div
                    className="wallet-modal-overlay"
                    onMouseDown={(e) => {
                        if (
                            e.target ===
                            e.currentTarget
                        ) {
                            setShowSubscriptionModal(
                                false
                            );
                        }
                    }}
                >

                    <div className="wallet-modal subscription-modal">

                        <div className="wallet-modal-header">

                            <div>
                                <h2>
                                    Member Subscription
                                </h2>

                                <p>
                                    Use branch wallet
                                    balance for member
                                    subscription.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    setShowSubscriptionModal(
                                        false
                                    )
                                }
                            >
                                <X size={20} />
                            </button>

                        </div>

                        <form
                            className="wallet-form"
                            onSubmit={
                                handleSubscription
                            }
                        >

                            <div className="subscription-wallet-balance">

                                <div>
                                    <Wallet
                                        size={20}
                                    />

                                    <span>
                                        Available
                                        Wallet
                                    </span>
                                </div>

                                <strong>
                                    ₹
                                    {wallet.balance.toLocaleString(
                                        "en-IN"
                                    )}
                                </strong>

                            </div>

                            <div className="wallet-form-group">

                                <label>
                                    Member
                                    <span>*</span>
                                </label>

                                <select
                                    name="memberId"
                                    value={
                                        subscriptionForm.memberId
                                    }
                                    onChange={
                                        handleSubscriptionChange
                                    }
                                    required
                                >
                                    <option value="">
                                        Select Member
                                    </option>

                                    {members.map(
                                        (member) => (
                                            <option
                                                key={
                                                    member.id
                                                }
                                                value={
                                                    member.id
                                                }
                                            >
                                                {
                                                    member.id
                                                }{" "}
                                                -{" "}
                                                {
                                                    member.name
                                                }
                                            </option>
                                        )
                                    )}

                                </select>

                            </div>

                            {selectedMember && (
                                <div className="selected-member-card">

                                    <div className="selected-member-avatar">
                                        <UserRound
                                            size={18}
                                        />
                                    </div>

                                    <div>
                                        <strong>
                                            {
                                                selectedMember.name
                                            }
                                        </strong>

                                        <span>
                                            {
                                                selectedMember.id
                                            }{" "}
                                            •{" "}
                                            {
                                                selectedMember.mobile
                                            }
                                        </span>
                                    </div>

                                </div>
                            )}

                            <div className="wallet-form-group">

                                <label>
                                    Membership Plan
                                    <span>*</span>
                                </label>

                                <select
                                    name="plan"
                                    value={
                                        subscriptionForm.plan
                                    }
                                    onChange={
                                        handleSubscriptionChange
                                    }
                                    required
                                >
                                    <option value="">
                                        Select Plan
                                    </option>

                                    {membershipPlans.map(
                                        (plan) => (
                                            <option
                                                key={
                                                    plan.id
                                                }
                                                value={
                                                    plan.name
                                                }
                                            >
                                                {
                                                    plan.name
                                                }{" "}
                                                - ₹
                                                {plan.amount.toLocaleString(
                                                    "en-IN"
                                                )}
                                            </option>
                                        )
                                    )}

                                </select>

                            </div>

                            {selectedPlan && (
                                <div className="selected-plan-card">

                                    <div className="selected-plan-icon">
                                        <Crown
                                            size={20}
                                        />
                                    </div>

                                    <div>
                                        <span>
                                            Selected Plan
                                        </span>

                                        <strong>
                                            {
                                                selectedPlan.name
                                            }
                                        </strong>
                                    </div>

                                    <div className="selected-plan-price">
                                        ₹
                                        {selectedPlan.amount.toLocaleString(
                                            "en-IN"
                                        )}
                                    </div>

                                </div>
                            )}

                            {selectedPlan && (
                                <div className="subscription-summary">

                                    <div>
                                        <span>
                                            Current
                                            Balance
                                        </span>

                                        <strong>
                                            ₹
                                            {wallet.balance.toLocaleString(
                                                "en-IN"
                                            )}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>
                                            Subscription
                                        </span>

                                        <strong className="debit">
                                            - ₹
                                            {selectedPlan.amount.toLocaleString(
                                                "en-IN"
                                            )}
                                        </strong>
                                    </div>

                                    <div className="after-balance">
                                        <span>
                                            Balance After
                                        </span>

                                        <strong>
                                            ₹
                                            {Math.max(
                                                wallet.balance -
                                                    selectedPlan.amount,
                                                0
                                            ).toLocaleString(
                                                "en-IN"
                                            )}
                                        </strong>
                                    </div>

                                </div>
                            )}

                            <div className="wallet-form-footer">

                                <button
                                    type="button"
                                    className="wallet-cancel-btn"
                                    onClick={() =>
                                        setShowSubscriptionModal(
                                            false
                                        )
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="wallet-submit-btn"
                                    disabled={
                                        !selectedPlan ||
                                        wallet.balance <
                                            (selectedPlan?.amount ||
                                                0)
                                    }
                                >
                                    <CreditCard
                                        size={16}
                                    />
                                    Use Wallet & Subscribe
                                </button>

                            </div>

                        </form>

                    </div>

                </div>
            )}

            {/* =================================================
                TRANSACTION VIEW MODAL
            ================================================= */}

            {showTransactionModal &&
                selectedTransaction && (
                    <div
                        className="wallet-modal-overlay"
                        onMouseDown={(e) => {
                            if (
                                e.target ===
                                e.currentTarget
                            ) {
                                setShowTransactionModal(
                                    false
                                );
                            }
                        }}
                    >

                        <div className="wallet-modal transaction-modal">

                            <div className="wallet-modal-header">

                                <div>
                                    <h2>
                                        Transaction Details
                                    </h2>

                                    <p>
                                        {
                                            selectedTransaction.transactionId
                                        }
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowTransactionModal(
                                            false
                                        )
                                    }
                                >
                                    <X size={20} />
                                </button>

                            </div>

                            <div className="transaction-view">

                                <div className="transaction-status-large">

                                    <div
                                        className={
                                            selectedTransaction.type ===
                                            "Recharge"
                                                ? "credit"
                                                : "debit"
                                        }
                                    >
                                        {selectedTransaction.type ===
                                        "Recharge" ? (
                                            <ArrowDownToLine
                                                size={
                                                    25
                                                }
                                            />
                                        ) : (
                                            <ArrowUpFromLine
                                                size={
                                                    25
                                                }
                                            />
                                        )}
                                    </div>

                                    <span>
                                        {
                                            selectedTransaction.type
                                        }
                                    </span>

                                    <strong
                                        className={
                                            selectedTransaction.type ===
                                            "Recharge"
                                                ? "credit-text"
                                                : "debit-text"
                                        }
                                    >
                                        {selectedTransaction.type ===
                                        "Recharge"
                                            ? "+"
                                            : "-"}
                                        ₹
                                        {selectedTransaction.amount.toLocaleString(
                                            "en-IN"
                                        )}
                                    </strong>

                                </div>

                                <div className="transaction-detail-grid">

                                    <TransactionDetail
                                        icon={
                                            Receipt
                                        }
                                        label="Transaction ID"
                                        value={
                                            selectedTransaction.transactionId
                                        }
                                    />

                                    <TransactionDetail
                                        icon={
                                            CalendarDays
                                        }
                                        label="Date"
                                        value={`${selectedTransaction.date} ${selectedTransaction.time}`}
                                    />

                                    <TransactionDetail
                                        icon={
                                            CreditCard
                                        }
                                        label="Payment Method"
                                        value={
                                            selectedTransaction.paymentMethod
                                        }
                                    />

                                    <TransactionDetail
                                        icon={
                                            CheckCircle2
                                        }
                                        label="Status"
                                        value={
                                            selectedTransaction.status
                                        }
                                    />

                                    <TransactionDetail
                                        icon={
                                            UserRound
                                        }
                                        label="Member"
                                        value={
                                            selectedTransaction.memberName !==
                                            "-"
                                                ? `${selectedTransaction.memberName} (${selectedTransaction.memberId})`
                                                : "-"
                                        }
                                    />

                                    <TransactionDetail
                                        icon={
                                            Crown
                                        }
                                        label="Membership Plan"
                                        value={
                                            selectedTransaction.plan
                                        }
                                    />

                                    <TransactionDetail
                                        icon={
                                            ArrowDownToLine
                                        }
                                        label="Balance Before"
                                        value={`₹${selectedTransaction.balanceBefore.toLocaleString(
                                            "en-IN"
                                        )}`}
                                    />

                                    <TransactionDetail
                                        icon={
                                            ArrowUpFromLine
                                        }
                                        label="Balance After"
                                        value={`₹${selectedTransaction.balanceAfter.toLocaleString(
                                            "en-IN"
                                        )}`}
                                    />

                                    <TransactionDetail
                                        icon={
                                            Receipt
                                        }
                                        label="Reference Number"
                                        value={
                                            selectedTransaction.referenceNo
                                        }
                                    />

                                </div>

                                <div className="transaction-description">

                                    <span>
                                        Description
                                    </span>

                                    <p>
                                        {
                                            selectedTransaction.description
                                        }
                                    </p>

                                </div>

                            </div>

                            <div className="wallet-modal-footer-simple">

                                <button
                                    type="button"
                                    className="wallet-cancel-btn"
                                    onClick={() =>
                                        setShowTransactionModal(
                                            false
                                        )
                                    }
                                >
                                    Close
                                </button>

                            </div>

                        </div>

                    </div>
                )}

        </div>
    );
}

/* =========================================================
   SUMMARY CARD
========================================================= */

function SummaryCard({
    icon: Icon,
    title,
    value,
    type,
}) {
    return (
        <div className="wallet-summary-card">

            <div
                className={`wallet-summary-icon ${type}`}
            >
                <Icon size={21} />
            </div>

            <div>
                <span>{title}</span>
                <strong>{value}</strong>
            </div>

        </div>
    );
}

/* =========================================================
   TRANSACTION DETAIL
========================================================= */

function TransactionDetail({
    icon: Icon,
    label,
    value,
}) {
    return (
        <div className="transaction-detail">

            <div>
                <Icon size={16} />
            </div>

            <section>
                <span>{label}</span>
                <strong>{value || "-"}</strong>
            </section>

        </div>
    );
}

export default Wallets;